import {useRef, useState} from 'react';

import {isNotNil} from 'ramda';

import {PendingEditor, PendingEditorChangeHandler} from '../types/PendingEditor';

interface StablePendingEditor {
  /** `true` while an editor holds unsaved changes. Re-renders only on the null↔non-null edge. */
  hasPendingEditor: boolean;
  /** One object with a stable identity for the whole lifetime of the owning component. */
  pendingEditor: PendingEditor;
  /** Records the latest editor and notifies the parent only when the pending state flips. */
  publishPendingEditor: (editor: PendingEditor | null) => void;
}

/**
 * Publishes the pending-editor *transition* instead of the editor object.
 *
 * A form or draft engine produces a fresh `{save, discard}` pair on every keystroke. Passing that
 * object up through `useState` hops re-renders every component on the way (both financing panels
 * and both matrices) for a single character. Here the latest pair is kept in a ref and the parent
 * receives one stable object, so the parent re-renders only when the pending state appears or
 * disappears. `save`/`discard` always reach the most recently published pair.
 */
export function useStablePendingEditor(
  onPendingChange?: PendingEditorChangeHandler
): StablePendingEditor {
  const latestEditorRef = useRef<PendingEditor | null>(null);
  const [hasPendingEditor, setHasPendingEditor] = useState(false);
  // Created once and never replaced, so every consumer above keeps the same object identity.
  const [stableEditor] = useState<PendingEditor>(() => ({
    save: () => latestEditorRef.current?.save() ?? Promise.resolve(true),
    discard: () => latestEditorRef.current?.discard(),
  }));

  const publishPendingEditor = (editor: PendingEditor | null) => {
    const hadPendingEditor = isNotNil(latestEditorRef.current);
    const hasEditor = isNotNil(editor);

    latestEditorRef.current = editor;

    if (hadPendingEditor === hasEditor) {
      return;
    }

    setHasPendingEditor(hasEditor);
    onPendingChange?.(hasEditor ? stableEditor : null);
  };

  return {hasPendingEditor, pendingEditor: stableEditor, publishPendingEditor};
}
